import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { 
  ArrowLeft, 
  Edit, 
  Trash2, 
  Copy, 
  Eye, 
  EyeOff,
  ExternalLink,
  Lock,
  Key,
  Calendar,
  Tag
} from 'lucide-react';
import { theme } from '../../styles/theme';
import { Card } from '../../components/UI/Card';
import { Button } from '../../components/UI/Button';
import { Input } from '../../components/UI/Input';
import { Textarea } from '../../components/UI/Textarea';
import { Modal } from '../../components/UI/Modal';
import LabelSelector from '../../components/UI/LabelSelector';
import { useApiOnMount, useApi } from '../../hooks/useApi';
import { secretService } from '../../services/secretService';
import { formatDate, getSecretTypeIcon, getSecretTypeLabel } from '../../utils/helpers';
import toast from 'react-hot-toast';

const SecretDetailContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.xl};
  max-width: 800px;
  margin: 0 auto;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: ${({ theme }) => theme.spacing.lg};
  
  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    flex-direction: column;
    align-items: flex-start;
    gap: ${({ theme }) => theme.spacing.md};
  }
`;

const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.md};
`;

const BackButton = styled(Button)`
  padding: ${({ theme }) => theme.spacing.sm};
`;

const SecretIcon = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSize['2xl']};
  margin-right: ${({ theme }) => theme.spacing.md};
`;

const SecretInfo = styled.div`
  h1 {
    font-size: ${({ theme }) => theme.typography.fontSize['2xl']};
    font-weight: ${({ theme }) => theme.typography.fontWeight.bold};
    color: ${({ theme }) => theme.colors.text};
    margin: 0 0 ${({ theme }) => theme.spacing.xs} 0;
  }
  
  p {
    font-size: ${({ theme }) => theme.typography.fontSize.sm};
    color: ${({ theme }) => theme.colors.textSecondary};
    margin: 0;
  }
`;

const HeaderRight = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.md};
`;

const ContentGrid = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: ${({ theme }) => theme.spacing.xl};
  
  @media (max-width: ${({ theme }) => theme.breakpoints.lg}) {
    grid-template-columns: 1fr;
  }
`;

const MainContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.lg};
`;

const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.md};
`;

const SectionTitle = styled.h2`
  font-size: ${({ theme }) => theme.typography.fontSize.lg};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.text};
  margin: 0;
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm};
`;

const FieldGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.sm};
`;

const FieldLabel = styled.label`
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  color: ${({ theme }) => theme.colors.gray700};
`;

const FieldValue = styled.div`
  padding: ${({ theme }) => theme.spacing.md};
  background-color: ${({ theme }) => theme.colors.gray50};
  border: 1px solid ${({ theme }) => theme.colors.gray200};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.text};
  word-break: break-all;
  position: relative;
`;

const SensitiveField = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm};
`;

const SensitiveValue = styled.span`
  flex: 1;
  font-family: ${({ theme }) => theme.typography.fontFamily.mono};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.text};
  word-break: break-all;
`;

const SensitiveActions = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xs};
`;

const ActionButton = styled.button`
  background: none;
  border: none;
  color: ${({ theme }) => theme.colors.gray500};
  cursor: pointer;
  padding: ${({ theme }) => theme.spacing.xs};
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  transition: all 0.2s ease-in-out;
  
  &:hover {
    background-color: ${({ theme }) => theme.colors.gray100};
    color: ${({ theme }) => theme.colors.gray700};
  }
`;

const CopyButton = styled(Button)`
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
  padding: ${({ theme }) => theme.spacing.xs} ${({ theme }) => theme.spacing.sm};
`;

const LinkButton = styled(Button)`
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
  padding: ${({ theme }) => theme.spacing.xs} ${({ theme }) => theme.spacing.sm};
`;

const Sidebar = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.lg};
`;

const MetaCard = styled(Card)`
  padding: ${({ theme }) => theme.spacing.lg};
`;

const MetaItem = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm};
  margin-bottom: ${({ theme }) => theme.spacing.md};
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const MetaLabel = styled.span`
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.textSecondary};
  min-width: 80px;
`;

const MetaValue = styled.span`
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.text};
`;

const LabelsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing.sm};
`;

const Label = styled.span`
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
  padding: ${({ theme }) => theme.spacing.xs} ${({ theme }) => theme.spacing.sm};
  background-color: ${({ $color }) => $color}20;
  color: ${({ $color }) => $color};
  border-radius: ${({ theme }) => theme.borderRadius.full};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
`;

const EmptyState = styled.div`
  text-align: center;
  padding: ${({ theme }) => theme.spacing['3xl']};
  color: ${({ theme }) => theme.colors.textSecondary};
  
  h3 {
    font-size: ${({ theme }) => theme.typography.fontSize.xl};
    font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
    margin: 0 0 ${({ theme }) => theme.spacing.sm} 0;
  }
  
  p {
    font-size: ${({ theme }) => theme.typography.fontSize.base};
    margin: 0;
  }
`;

function SecretDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const [showSecretKey, setShowSecretKey] = useState(false);
  const [password, setPassword] = useState('');
  const [secretKey, setSecretKey] = useState('');
  const [showEditModal, setShowEditModal] = useState(false);
  
  const { data: secret, loading, refetch } = useApiOnMount(
    () => secretService.getSecret(id)
  );
  const { execute: updateSecret, loading: updating } = useApi(secretService.updateSecret);
  const { execute: deleteSecret, loading: deleting } = useApi(secretService.deleteSecret);
  const { execute: getPassword } = useApi(secretService.getDecryptedPassword);
  const { execute: getSecretKey } = useApi(secretService.getDecryptedSecretKey);

  const handleEdit = () => {
    setShowEditModal(true);
  };

  const handleDelete = async () => {
    if (window.confirm('Are you sure you want to delete this secret? This action cannot be undone.')) {
      try {
        await deleteSecret(id);
        toast.success('Secret deleted successfully');
        // Dispatch event to refresh label counts
        window.dispatchEvent(new CustomEvent('secretChanged'));
        navigate('/spaces');
      } catch (error) {
        toast.error('Failed to delete secret');
      }
    }
  };

  const handleUpdateSecret = async (data) => {
    try {
      // Extract label_ids from data
      const { label_ids, ...secretData } = data;
      
      // Update the secret
      await updateSecret(id, secretData);
      
      // Update labels only if they have actually changed
      if (label_ids !== undefined) {
        const currentLabelIds = (secret?.labels?.map(l => l.id) || []).sort();
        const newLabelIds = (label_ids || []).sort();
        
        // Check if labels have changed by comparing sorted arrays
        const labelsChanged = JSON.stringify(currentLabelIds) !== JSON.stringify(newLabelIds);
        
        if (labelsChanged) {
          // Filter out invalid label IDs (0, null, undefined, etc.)
          const validLabelIds = newLabelIds.filter(id => id && id > 0);
          await secretService.assignLabels(id, validLabelIds);
          // Dispatch event to refresh label counts
          window.dispatchEvent(new CustomEvent('secretChanged'));
        }
      }
      
      toast.success('Secret updated successfully');
      setShowEditModal(false);
      refetch();
    } catch (error) {
      toast.error('Failed to update secret');
    }
  };

  const handleCopyPassword = async () => {
    try {
      if (!password) {
        const response = await getPassword(id);
        setPassword(response.data.password);
      }
      await navigator.clipboard.writeText(password);
      toast.success('Password copied to clipboard');
    } catch (error) {
      toast.error('Failed to copy password');
    }
  };

  const handleCopySecretKey = async () => {
    try {
      if (!secretKey) {
        const response = await getSecretKey(id);
        setSecretKey(response.data.secret_key);
      }
      await navigator.clipboard.writeText(secretKey);
      toast.success('Secret key copied to clipboard');
    } catch (error) {
      toast.error('Failed to copy secret key');
    }
  };

  const handleTogglePassword = async () => {
    if (!showPassword && !password) {
      try {
        const response = await getPassword(id);
        setPassword(response.data.password);
      } catch (error) {
        toast.error('Failed to load password');
        return;
      }
    }
    setShowPassword(!showPassword);
  };

  const handleToggleSecretKey = async () => {
    if (!showSecretKey && !secretKey) {
      try {
        const response = await getSecretKey(id);
        setSecretKey(response.data.secret_key);
      } catch (error) {
        toast.error('Failed to load secret key');
        return;
      }
    }
    setShowSecretKey(!showSecretKey);
  };

  const handleOpenLink = () => {
    if (secret?.link) {
      window.open(secret.link, '_blank', 'noopener,noreferrer');
    }
  };

  if (loading) {
    return <div>Loading secret...</div>;
  }

  if (!secret) {
    return (
      <EmptyState>
        <h3>Secret not found</h3>
        <p>The secret you're looking for doesn't exist or you don't have access to it.</p>
      </EmptyState>
    );
  }

  return (
    <SecretDetailContainer>
      <Header>
        <HeaderLeft>
          <BackButton variant="ghost" onClick={() => navigate(-1)}>
            <ArrowLeft size={16} />
          </BackButton>
          <SecretIcon>
            {getSecretTypeIcon(secret.type)}
          </SecretIcon>
          <SecretInfo>
            <h1>{secret.title}</h1>
            <p>{getSecretTypeLabel(secret.type)}</p>
          </SecretInfo>
        </HeaderLeft>
        <HeaderRight>
          <Button variant="secondary" onClick={handleEdit}>
            <Edit size={16} />
            Edit
          </Button>
          <Button variant="danger" onClick={handleDelete} loading={deleting}>
            <Trash2 size={16} />
            Delete
          </Button>
        </HeaderRight>
      </Header>

      <ContentGrid>
        <MainContent>
          <Section>
            <SectionTitle>
              <Lock size={20} />
              Credentials
            </SectionTitle>
            
            {secret.username && (
              <FieldGroup>
                <FieldLabel>Username</FieldLabel>
                <FieldValue>
                  <SensitiveField>
                    <SensitiveValue>{secret.username}</SensitiveValue>
                    <SensitiveActions>
                      <ActionButton onClick={() => navigator.clipboard.writeText(secret.username)}>
                        <Copy size={14} />
                      </ActionButton>
                    </SensitiveActions>
                  </SensitiveField>
                </FieldValue>
              </FieldGroup>
            )}

            {secret.password && (
              <FieldGroup>
                <FieldLabel>Password</FieldLabel>
                <FieldValue>
                  <SensitiveField>
                    <SensitiveValue>
                      {showPassword ? password : '••••••••••••••••'}
                    </SensitiveValue>
                    <SensitiveActions>
                      <ActionButton onClick={handleTogglePassword}>
                        {showPassword ? <EyeOff size={14} /> : <Eye size={14} />}
                      </ActionButton>
                      <CopyButton variant="ghost" size="sm" onClick={handleCopyPassword}>
                        <Copy size={14} />
                      </CopyButton>
                    </SensitiveActions>
                  </SensitiveField>
                </FieldValue>
              </FieldGroup>
            )}

            {secret.secret_key && (
              <FieldGroup>
                <FieldLabel>Secret Key</FieldLabel>
                <FieldValue>
                  <SensitiveField>
                    <SensitiveValue>
                      {showSecretKey ? secretKey : '••••••••••••••••'}
                    </SensitiveValue>
                    <SensitiveActions>
                      <ActionButton onClick={handleToggleSecretKey}>
                        {showSecretKey ? <EyeOff size={14} /> : <Eye size={14} />}
                      </ActionButton>
                      <CopyButton variant="ghost" size="sm" onClick={handleCopySecretKey}>
                        <Copy size={14} />
                      </CopyButton>
                    </SensitiveActions>
                  </SensitiveField>
                </FieldValue>
              </FieldGroup>
            )}
          </Section>

          <Section>
            <SectionTitle>
              <Key size={20} />
              Details
            </SectionTitle>
            
            {secret.description && (
              <FieldGroup>
                <FieldLabel>Description</FieldLabel>
                <FieldValue>{secret.description}</FieldValue>
              </FieldGroup>
            )}

            {secret.link && (
              <FieldGroup>
                <FieldLabel>Link</FieldLabel>
                <FieldValue>
                  <SensitiveField>
                    <SensitiveValue>{secret.link}</SensitiveValue>
                    <SensitiveActions>
                      <LinkButton variant="ghost" size="sm" onClick={handleOpenLink}>
                        <ExternalLink size={14} />
                      </LinkButton>
                    </SensitiveActions>
                  </SensitiveField>
                </FieldValue>
              </FieldGroup>
            )}

            {secret.domain && (
              <FieldGroup>
                <FieldLabel>Domain</FieldLabel>
                <FieldValue>{secret.domain}</FieldValue>
              </FieldGroup>
            )}
          </Section>
        </MainContent>

        <Sidebar>
          <MetaCard>
            <SectionTitle>
              <Calendar size={20} />
              Information
            </SectionTitle>
            
            <MetaItem>
              <MetaLabel>Type</MetaLabel>
              <MetaValue>{getSecretTypeLabel(secret.type)}</MetaValue>
            </MetaItem>
            
            <MetaItem>
              <MetaLabel>Created</MetaLabel>
              <MetaValue>{formatDate(secret.created_at)}</MetaValue>
            </MetaItem>
            
            <MetaItem>
              <MetaLabel>Updated</MetaLabel>
              <MetaValue>{formatDate(secret.updated_at)}</MetaValue>
            </MetaItem>
          </MetaCard>

          {secret.labels && secret.labels.length > 0 && (
            <MetaCard>
              <SectionTitle>
                <Tag size={20} />
                Labels
              </SectionTitle>
              <LabelsContainer>
                {secret.labels.map((label) => (
                  <Label key={label.id} $color={label.color}>
                    {label.name}
                  </Label>
                ))}
              </LabelsContainer>
            </MetaCard>
          )}
        </Sidebar>
      </ContentGrid>

      {/* Edit Modal */}
      <SecretEditModal
        isOpen={showEditModal}
        onClose={() => setShowEditModal(false)}
        onSubmit={handleUpdateSecret}
        initialData={secret}
        loading={updating}
      />
    </SecretDetailContainer>
  );
}

export default SecretDetailPage;

// Secret Edit Modal Component
function SecretEditModal({ isOpen, onClose, onSubmit, initialData, loading }) {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    link: '',
    domain: '',
    username: '',
    password: '',
    secret_key: '',
    type: 'login',
    ...initialData
  });
  const [selectedLabelIds, setSelectedLabelIds] = useState(initialData?.labels?.map(l => l.id) || []);

  // Update form data when initialData changes
  useEffect(() => {
    if (initialData) {
      setFormData({
        title: initialData.title || '',
        description: initialData.description || '',
        link: initialData.link || '',
        domain: initialData.domain || '',
        username: initialData.username || '',
        password: '', // Don't pre-fill password for security
        secret_key: '', // Don't pre-fill secret key for security
        type: initialData.type || 'login',
      });
      setSelectedLabelIds(initialData.labels?.map(l => l.id) || []);
    } else {
      // Reset form for new secret
      setFormData({
        title: '',
        description: '',
        link: '',
        domain: '',
        username: '',
        password: '',
        secret_key: '',
        type: 'login',
      });
      setSelectedLabelIds([]);
    }
  }, [initialData]);

  const handleSubmit = (e) => {
    e.preventDefault();
    // Filter out invalid label IDs (0, null, undefined, etc.)
    const validLabelIds = selectedLabelIds.filter(id => id && id > 0);
    onSubmit({ ...formData, label_ids: validLabelIds });
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const secretTypes = {
    login: 'Login',
    password: 'Password',
    api_key: 'API Key',
    note: 'Note',
    card: 'Card',
    ssh: 'SSH Key',
    database: 'Database',
    other: 'Other',
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Edit Secret"
      maxWidth="600px"
    >
      <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: theme.spacing.lg }}>
        <Input
          label="Title"
          name="title"
          value={formData.title}
          onChange={handleChange}
          placeholder="Enter secret title"
          required
        />
        
        <Textarea
          label="Description"
          name="description"
          value={formData.description}
          onChange={handleChange}
          placeholder="Enter secret description (optional)"
          rows={3}
        />
        
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: theme.spacing.md }}>
          <Input
            label="Link"
            name="link"
            value={formData.link}
            onChange={handleChange}
            placeholder="https://example.com"
            type="url"
          />
          
          <Input
            label="Domain"
            name="domain"
            value={formData.domain}
            onChange={handleChange}
            placeholder="example.com"
          />
        </div>
        
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: theme.spacing.md }}>
          <Input
            label="Username"
            name="username"
            value={formData.username}
            onChange={handleChange}
            placeholder="Enter username"
          />
          
          <Input
            label="Password"
            name="password"
            value={formData.password}
            onChange={handleChange}
            placeholder="Enter password"
            type="password"
          />
        </div>
        
        <Input
          label="Secret Key"
          name="secret_key"
          value={formData.secret_key}
          onChange={handleChange}
          placeholder="Enter secret key or API key"
        />
        
        <div>
          <label style={{ 
            fontSize: theme.typography.fontSize.sm,
            fontWeight: theme.typography.fontWeight.medium,
            color: theme.colors.gray700,
            marginBottom: theme.spacing.sm,
            display: 'block'
          }}>
            Type
          </label>
          <select
            name="type"
            value={formData.type}
            onChange={handleChange}
            style={{
              width: '100%',
              padding: theme.spacing.sm,
              border: `1px solid ${theme.colors.gray300}`,
              borderRadius: theme.borderRadius.md,
              fontSize: theme.typography.fontSize.base,
            }}
          >
            {Object.entries(secretTypes).map(([value, label]) => (
              <option key={value} value={value}>{label}</option>
            ))}
          </select>
        </div>

        <LabelSelector
          selectedLabelIds={selectedLabelIds}
          onLabelsChange={setSelectedLabelIds}
          disabled={loading}
        />

        <div style={{ 
          display: 'flex', 
          gap: theme.spacing.md, 
          justifyContent: 'flex-end',
          marginTop: theme.spacing.lg
        }}>
          <Button
            type="button"
            variant="secondary"
            onClick={onClose}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="primary"
            loading={loading}
            disabled={loading}
          >
            Update Secret
          </Button>
        </div>
      </form>
    </Modal>
  );
}
